// API credentials (FR-43, FR-47; company_admin only). The client secret is
// shown once in component state and never enters the query cache. While the
// company is pending, only sandbox credentials can be issued (the server
// answers 403 for live ones).
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { companyKey, ConfirmDialog, EnvironmentBadge, SecretOnce, type Company } from "./common";

type Scope = Schemas["Scope"];
type Credential = Schemas["Credential"];
type Issued = Schemas["IssuedCredential"];

const SCOPES: Scope[] = ["resolve", "stk", "one_time", "presence", "collect", "notify"];

export function CredentialsTab({ company }: { company: Company }) {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const key = companyKey(company.id, "credentials");
  const credentials = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(api.GET("/companies/{company_id}/credentials", { params: { path: { company_id: company.id } } })),
  });
  const [issued, setIssued] = useState<Issued | null>(null);
  const [revoking, setRevoking] = useState<Credential | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const items = credentials.data?.items ?? [];
  const scopeLabel = (s: Scope) => t(`scope.${s}`);

  const revoke = async () => {
    if (!revoking) return;
    setRevokeError(null);
    setBusy(true);
    const { error, response } = await api.DELETE("/companies/{company_id}/credentials/{credential_id}", {
      params: { path: { company_id: company.id, credential_id: revoking.id } },
    });
    setBusy(false);
    if (!response.ok) return setRevokeError(errorMessage(error, t("error.generic")));
    setNotice(t("credentials.revoked_notice", { label: revoking.label || revoking.client_id }));
    setRevoking(null);
    void queryClient.invalidateQueries({ queryKey: key });
  };

  return (
    <Stack gap="md">
      <Text size="sm">
        {t("credentials.intro")}{" "}
        <Anchor component={Link} to="/developers">
          {t("credentials.docs")}
        </Anchor>
      </Text>
      {company.status === "pending" && (
        <Alert color="yellow" title={t("credentials.pending_title")} data-testid="credentials-pending">
          {t("credentials.pending_body")}
        </Alert>
      )}
      {notice && <Notice>{notice}</Notice>}
      {issued ? (
        <SecretOnce
          title={t("credentials.issued_title", { label: issued.label || issued.client_id })}
          values={[
            { key: "client_id", label: t("credentials.client_id"), value: issued.client_id },
            { key: "client_secret", label: t("credentials.client_secret"), value: issued.client_secret },
          ]}
          onDone={() => setIssued(null)}
        />
      ) : (
        <CreateCredential
          company={company}
          onIssued={(c) => {
            setNotice(null);
            setIssued(c);
            void queryClient.invalidateQueries({ queryKey: key });
          }}
        />
      )}
      <Title order={2} size="h4">
        {t("credentials.list")}
      </Title>
      {credentials.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : credentials.isError ? (
        <FormError message={errorMessage(credentials.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="credentials-empty">
          {t("credentials.empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table verticalSpacing="sm" aria-label={t("credentials.list")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("credentials.label")}</Table.Th>
                <Table.Th>{t("credentials.client_id")}</Table.Th>
                <Table.Th>{t("credentials.scopes")}</Table.Th>
                <Table.Th>{t("credentials.environment")}</Table.Th>
                <Table.Th>{t("credentials.last_used")}</Table.Th>
                <Table.Th>{t("staff.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((c) => (
                <Table.Tr key={c.id} data-testid="credential-row">
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {c.label || "—"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {t("credentials.created", { date: f.date(c.created_at) })}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" style={{ wordBreak: "break-all" }}>
                      {c.client_id}
                    </Text>
                  </Table.Td>
                  <Table.Td>{c.scopes.map(scopeLabel).join(", ")}</Table.Td>
                  <Table.Td>
                    <EnvironmentBadge sandbox={c.sandbox} />
                  </Table.Td>
                  <Table.Td>{c.last_used_at ? f.dateTime(c.last_used_at) : t("credentials.never_used")}</Table.Td>
                  <Table.Td>
                    {c.revoked_at ? (
                      <Badge color="red" variant="light">
                        {t("credentials.revoked_on", { date: f.date(c.revoked_at) })}
                      </Badge>
                    ) : (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          setRevokeError(null);
                          setRevoking(c);
                        }}
                        aria-label={t("credentials.revoke_for", { label: c.label || c.client_id })}
                      >
                        {t("credentials.revoke")}
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <ConfirmDialog
        opened={revoking !== null}
        title={t("credentials.revoke_title")}
        confirmLabel={t("credentials.revoke")}
        busy={busy}
        error={revokeError}
        onConfirm={() => void revoke()}
        onCancel={() => setRevoking(null)}
      >
        {t("credentials.revoke_body", { label: revoking?.label || revoking?.client_id || "" })}
      </ConfirmDialog>
    </Stack>
  );
}

function CreateCredential({ company, onIssued }: { company: Company; onIssued: (c: Issued) => void }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState<Scope[]>(["resolve"]);
  const [sandbox, setSandbox] = useState(company.status !== "active");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (scopes.length === 0) return setError(t("credentials.pick_scope"));
    setError(null);
    setBusy(true);
    const { data, error } = await api.POST("/companies/{company_id}/credentials", {
      params: { path: { company_id: company.id } },
      body: { label: label.trim(), scopes, sandbox },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setLabel("");
    onIssued(data);
  };

  return (
    <Paper withBorder p="md" radius="md">
      <form onSubmit={submit} data-testid="credential-form">
        <Stack gap="sm">
          <Title order={2} size="h4">
            {t("credentials.create")}
          </Title>
          <TextInput
            label={t("credentials.label")}
            description={t("credentials.label_hint")}
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            maxLength={100}
          />
          <Checkbox.Group
            label={t("credentials.scopes")}
            value={scopes}
            onChange={(v) => setScopes(SCOPES.filter((s) => v.includes(s)))}
          >
            <Stack gap={6} mt={6}>
              {SCOPES.map((s) => (
                <Checkbox key={s} value={s} label={t(`scope.${s}`)} description={t(`scope.${s}_hint`)} />
              ))}
            </Stack>
          </Checkbox.Group>
          <Checkbox
            label={t("credentials.sandbox")}
            description={t("credentials.sandbox_hint")}
            checked={sandbox}
            onChange={(e) => setSandbox(e.currentTarget.checked)}
          />
          <FormError message={error} />
          <Button type="submit" loading={busy} w="fit-content">
            {t("credentials.create_submit")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
